import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import api from '../../utils/api';

// Admin-only "Default Data Sources" panel (Admin Dashboard → Default Data
// Sources tab). Controls which default (auto) data sources join the nightly
// BigQuery sync, in three layers: a global default per source (all accounts),
// per-account overrides, and per-workspace overrides inside an account. On top
// of that, every account and every workspace has a master "daily sync" switch.
// Effective value = accountEnabled && workspaceEnabled &&
//   (workspaceOverride ?? accountOverride ?? globalOverride ?? catalogDefault).
// Every change is propagated to the already-seeded sources immediately and
// applied to future workspaces at seed time.
//
// Sibling of WorkspaceAgentsManager: same embedded-card chrome, same /admin/*
// API surface, same role === 'admin' gate on the server.

const GLOBAL_SCOPE = '';
const WHOLE_ACCOUNT = '';

const s = {
  embedded: { background: 'var(--surface,#fff)', borderRadius: '14px', width: '100%', maxWidth: '980px', border: '1px solid var(--border)', display: 'flex', flexDirection: 'column', overflow: 'hidden' },
  header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 22px', borderBottom: '1px solid var(--border)', flexShrink: 0 },
  title: { fontWeight: 700, fontSize: '15px', color: 'var(--text-primary)' },
  body: { padding: '20px 22px', display: 'flex', flexDirection: 'column', gap: '18px' },
  note: { fontSize: '12px', lineHeight: 1.6, color: 'var(--text-secondary)', background: '#f8fafc', border: '1px solid var(--border)', borderRadius: '10px', padding: '12px 14px', margin: 0 },
  filters: { display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' },
  select: { padding: '8px 10px', border: '1px solid var(--border)', borderRadius: '8px', fontSize: '13px', background: '#fff', minWidth: '240px' },
  scopeHint: { fontSize: '12px', color: 'var(--text-muted)' },
  masterBar: { display: 'flex', flexDirection: 'column', gap: '6px', padding: '10px 14px', border: '1px solid var(--border)', borderRadius: '10px', background: '#fbfcfe' },
  masterRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap', fontSize: '13px', color: 'var(--text-primary)' },
  masterState: (on) => ({ fontWeight: 700, color: on ? '#047857' : '#b91c1c' }),
  masterNote: { fontSize: '12px', lineHeight: 1.5, color: 'var(--text-muted)', margin: 0 },
  card: { border: '1px solid var(--border)', borderRadius: '10px', overflow: 'hidden' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '13px' },
  th: { textAlign: 'left', fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-muted)', padding: '10px 14px', background: '#fbfcfe', borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap' },
  td: { padding: '11px 14px', borderBottom: '1px solid var(--border)', color: 'var(--text-primary)', verticalAlign: 'middle' },
  groupRow: { padding: '9px 14px', background: '#f8fafc', borderBottom: '1px solid var(--border)', fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-secondary)' },
  name: { fontWeight: 600 },
  meta: { fontSize: '11px', color: 'var(--text-muted)' },
  defaultPill: (on) => ({
    display: 'inline-flex', alignItems: 'center', padding: '2px 9px', borderRadius: '99px',
    fontSize: '11px', fontWeight: 600, whiteSpace: 'nowrap',
    background: on ? '#ecfdf5' : '#f1f5f9',
    color: on ? '#047857' : '#64748b',
    border: `1px solid ${on ? '#a7f3d0' : 'var(--border)'}`,
  }),
  overriddenPill: { display: 'inline-flex', alignItems: 'center', padding: '2px 8px', borderRadius: '99px', background: '#fffbeb', color: '#92400e', border: '1px solid #fcd34d', fontSize: '10px', fontWeight: 700, whiteSpace: 'nowrap' },
  toggleBtn: (on, busy) => ({
    padding: '5px 14px', borderRadius: '8px', fontWeight: 600, fontSize: '12px',
    cursor: busy ? 'progress' : 'pointer', opacity: busy ? 0.6 : 1, whiteSpace: 'nowrap',
    border: `1px solid ${on ? 'var(--success, #057a55)' : 'var(--border)'}`,
    background: on ? 'var(--success-light, #dcfce7)' : '#fff',
    color: on ? 'var(--success, #057a55)' : 'var(--text-secondary)',
  }),
  resetBtn: { background: 'none', border: 'none', cursor: 'pointer', fontSize: '11px', color: 'var(--accent, #2563eb)', fontWeight: 600, padding: '2px 4px' },
  triSelect: (busy) => ({ padding: '6px 8px', border: '1px solid var(--border)', borderRadius: '8px', fontSize: '12px', background: '#fff', cursor: busy ? 'progress' : 'pointer', opacity: busy ? 0.6 : 1 }),
  controlWrap: (dimmed) => ({ display: 'inline-flex', alignItems: 'center', gap: '8px', opacity: dimmed ? 0.55 : 1 }),
  empty: { fontSize: '13px', color: 'var(--text-muted)', padding: '18px 14px' },
};

export default function DefaultSourcesManager() {
  const [stores, setStores] = useState([]);
  const [specs, setSpecs] = useState([]);
  const [globalOverrides, setGlobalOverrides] = useState({});
  const [scope, setScope] = useState(GLOBAL_SCOPE); // '' = global defaults, else a shopKey
  const [wsScope, setWsScope] = useState(WHOLE_ACCOUNT); // '' = whole account, else a workspaceId inside `scope`
  const [shopState, setShopState] = useState(null); // { enabled, overrides, effective, workspaces } for the selected shop
  const [wsState, setWsState] = useState(null); // { name, enabled, shopEnabled, overrides, inherited, effective } for the selected workspace
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState('');
  const [savingMaster, setSavingMaster] = useState(false);

  useEffect(() => {
    api.get('/admin/stores').then(({ data }) => setStores(data)).catch(() => toast.error('Failed to load stores'));
  }, []);

  useEffect(() => {
    setLoading(true);
    api.get('/admin/default-sources/catalog')
      .then(({ data }) => {
        setSpecs(data.specs || []);
        setGlobalOverrides(data.global?.overrides || {});
      })
      .catch((err) => toast.error(err?.response?.data?.error || 'Failed to load the default-source catalog'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (scope === GLOBAL_SCOPE) { setShopState(null); return; }
    let cancelled = false;
    setShopState(null);
    api.get(`/admin/default-sources/shops/${encodeURIComponent(scope)}`)
      .then(({ data }) => { if (!cancelled) setShopState(toShopState(data)); })
      .catch((err) => { if (!cancelled) toast.error(err?.response?.data?.error || 'Failed to load account overrides'); });
    return () => { cancelled = true; };
  }, [scope]);

  useEffect(() => {
    if (wsScope === WHOLE_ACCOUNT) { setWsState(null); return; }
    let cancelled = false;
    setWsState(null);
    api.get(`/admin/default-sources/workspaces/${encodeURIComponent(wsScope)}`)
      .then(({ data }) => { if (!cancelled) setWsState(toWsState(data)); })
      .catch((err) => { if (!cancelled) toast.error(err?.response?.data?.error || 'Failed to load workspace overrides'); });
    return () => { cancelled = true; };
  }, [wsScope]);

  const storeOptions = useMemo(
    () => [...stores].sort((a, b) => a.name.localeCompare(b.name)),
    [stores],
  );

  const workspaceOptions = useMemo(
    () => [...(shopState?.workspaces || [])].sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''))),
    [shopState],
  );

  const effectiveGlobal = (spec) => {
    const ov = globalOverrides[spec.autoKey];
    return typeof ov === 'boolean' ? ov : spec.specAutoSync;
  };

  // Shop PUT responses for a single source omit `workspaces`; keep the list we
  // already have so the workspace picker doesn't blink empty.
  const applyShopData = (data) => {
    setShopState((prev) => toShopState(data, prev));
  };

  // Workspace responses carry the truth for that workspace; mirror the bits the
  // account-level workspace picker shows (master + override count) so it stays
  // accurate without a refetch.
  const applyWsData = (data) => {
    const next = toWsState(data);
    setWsState(next);
    const overrideCount = Object.values(next.overrides).filter((v) => typeof v === 'boolean').length;
    setShopState((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        workspaces: (prev.workspaces || []).map((ws) => (
          String(ws.id) === String(data.workspaceId || wsScope)
            ? { ...ws, enabled: next.enabled, overrideCount }
            : ws
        )),
      };
    });
  };

  const saveGlobal = async (spec, enabled) => {
    setSavingKey(spec.autoKey);
    try {
      const { data } = await api.put('/admin/default-sources/global', { autoKey: spec.autoKey, enabled });
      setGlobalOverrides(data.overrides || {});
      const n = data.propagated?.modified ?? 0;
      toast.success(
        enabled === null
          ? `"${spec.name}" reverted to the catalog default — ${n} existing source(s) updated`
          : `Daily sync ${enabled ? 'enabled' : 'disabled'} for "${spec.name}" everywhere — ${n} existing source(s) updated`,
      );
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Failed to update the global default');
    } finally {
      setSavingKey('');
    }
  };

  const saveShop = async (spec, enabled) => {
    setSavingKey(spec.autoKey);
    try {
      const { data } = await api.put(`/admin/default-sources/shops/${encodeURIComponent(scope)}`, { autoKey: spec.autoKey, enabled });
      applyShopData(data);
      const n = data.propagated?.modified ?? 0;
      toast.success(
        enabled === null
          ? `"${spec.name}" now inherits the default for this account — ${n} source(s) updated`
          : `Daily sync ${enabled ? 'enabled' : 'disabled'} for "${spec.name}" on this account — ${n} source(s) updated`,
      );
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Failed to update the account override');
    } finally {
      setSavingKey('');
    }
  };

  const saveWorkspace = async (spec, enabled) => {
    setSavingKey(spec.autoKey);
    try {
      const { data } = await api.put(`/admin/default-sources/workspaces/${encodeURIComponent(wsScope)}`, { autoKey: spec.autoKey, enabled });
      applyWsData(data);
      const n = data.propagated?.modified ?? 0;
      toast.success(
        enabled === null
          ? `"${spec.name}" now inherits the account default for this workspace — ${n} source(s) updated`
          : `Daily sync ${enabled ? 'enabled' : 'disabled'} for "${spec.name}" in this workspace — ${n} source(s) updated`,
      );
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Failed to update the workspace override');
    } finally {
      setSavingKey('');
    }
  };

  const saveShopEnabled = async (enabled) => {
    setSavingMaster(true);
    try {
      const { data } = await api.put(`/admin/default-sources/shops/${encodeURIComponent(scope)}/enabled`, { enabled });
      applyShopData(data);
      const n = data.propagated?.modified ?? 0;
      toast.success(`Daily sync turned ${enabled ? 'on' : 'off'} for this account — ${n} source(s) updated`);
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Failed to update the account daily-sync switch');
    } finally {
      setSavingMaster(false);
    }
  };

  const saveWorkspaceEnabled = async (enabled) => {
    setSavingMaster(true);
    try {
      const { data } = await api.put(`/admin/default-sources/workspaces/${encodeURIComponent(wsScope)}/enabled`, { enabled });
      applyWsData(data);
      const n = data.propagated?.modified ?? 0;
      toast.success(`Daily sync turned ${enabled ? 'on' : 'off'} for this workspace — ${n} source(s) updated`);
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Failed to update the workspace daily-sync switch');
    } finally {
      setSavingMaster(false);
    }
  };

  const groups = useMemo(() => ([
    { label: 'Google Ads', specs: specs.filter((sp) => sp.platform === 'google') },
    { label: 'Meta Ads', specs: specs.filter((sp) => sp.platform === 'meta') },
  ]), [specs]);

  const shopScoped = scope !== GLOBAL_SCOPE;
  const wsScoped = shopScoped && wsScope !== WHOLE_ACCOUNT;
  const scopeReady = !shopScoped || (!!shopState && (!wsScoped || !!wsState));
  // The save paths apply their PUT response to whatever scope is selected when
  // it lands, so the pickers stay locked while a save is in flight — otherwise
  // a slow account-wide recompute could paint account A's payload over B.
  const saving = !!savingKey || savingMaster;

  // Master switch of whatever is selected, and whether anything in it can sync
  // at all (a workspace is also gated by its account's master).
  const masterOn = wsScoped ? !!wsState?.enabled : !!shopState?.enabled;
  const parentOff = wsScoped && wsState?.shopEnabled === false;
  const dimControls = shopScoped && (!masterOn || parentOff);

  const renderControl = (spec) => {
    const busy = savingKey === spec.autoKey;
    if (!shopScoped) {
      const on = effectiveGlobal(spec);
      const overridden = typeof globalOverrides[spec.autoKey] === 'boolean';
      return (
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
          {overridden && <span style={s.overriddenPill} title="Differs from the catalog default">overridden</span>}
          <button type="button" style={s.toggleBtn(on, busy)} disabled={busy} onClick={() => saveGlobal(spec, !on)}>
            {busy ? 'Saving…' : on ? 'On' : 'Off'}
          </button>
          {overridden && !busy && (
            <button type="button" style={s.resetBtn} onClick={() => saveGlobal(spec, null)} title="Revert to the catalog default">
              Reset
            </button>
          )}
        </div>
      );
    }

    // Account or workspace scope: tri-state select + the folded-in effective pill.
    const scoped = wsScoped ? wsState : shopState;
    const override = scoped?.overrides?.[spec.autoKey];
    const hasOverride = typeof override === 'boolean';
    const inheritedValue = wsScoped
      ? (typeof wsState?.inherited?.[spec.autoKey] === 'boolean' ? wsState.inherited[spec.autoKey] : effectiveGlobal(spec))
      : effectiveGlobal(spec);
    const effective = !!scoped?.effective?.[spec.autoKey];
    const value = hasOverride ? (override ? 'on' : 'off') : 'inherit';
    const save = wsScoped ? saveWorkspace : saveShop;
    return (
      <div style={s.controlWrap(dimControls)}>
        <span
          style={s.defaultPill(effective)}
          title={effective ? 'Syncs daily after masters and overrides are applied' : 'Will not sync daily — still available on demand'}
        >
          {effective ? 'Daily' : 'On demand'}
        </span>
        <select
          style={s.triSelect(busy)}
          disabled={busy}
          value={value}
          onChange={(e) => {
            const v = e.target.value;
            save(spec, v === 'inherit' ? null : v === 'on');
          }}
        >
          <option value="inherit">Inherit ({inheritedValue ? 'On' : 'Off'})</option>
          <option value="on">On</option>
          <option value="off">Off</option>
        </select>
      </div>
    );
  };

  const renderMasterBar = () => {
    if (!shopScoped || !scopeReady) return null;
    const label = wsScoped ? 'Daily sync for this workspace' : 'Daily sync for this account';
    const toggle = wsScoped ? saveWorkspaceEnabled : saveShopEnabled;
    return (
      <div style={s.masterBar} data-testid="default-sources-master">
        <div style={s.masterRow}>
          <span>
            {label}: <span style={s.masterState(masterOn)}>{masterOn ? 'On' : 'Off'}</span>
          </span>
          <button
            type="button"
            data-testid="default-sources-master-toggle"
            style={s.toggleBtn(false, savingMaster)}
            disabled={savingMaster}
            onClick={() => toggle(!masterOn)}
            title={masterOn ? 'Stop every default source here from syncing daily' : 'Let default sources here sync daily again'}
          >
            {savingMaster ? 'Saving…' : masterOn ? 'Turn off' : 'Turn on'}
          </button>
        </div>
        {parentOff && (
          <p style={s.masterNote}>
            Daily sync is turned off for the whole account — turn it back on at the account level first.
            Until then nothing in this workspace syncs daily, whatever the switch and per-source settings below say.
          </p>
        )}
        {!parentOff && !masterOn && (
          <p style={s.masterNote}>
            No default source in this {wsScoped ? 'workspace' : 'account'} will sync daily regardless of the
            per-source settings below — they still sync on demand.
          </p>
        )}
      </div>
    );
  };

  const scopeHint = wsScoped
    ? 'Setting per-workspace overrides — "Inherit" follows the account (then global) default.'
    : shopScoped
      ? 'Setting per-account overrides — "Inherit" follows the global default.'
      : 'Setting the global default for every account.';

  return (
    <div style={s.embedded}>
      <div style={s.header}>
        <span style={s.title}>Default Data Sources</span>
      </div>

      <div style={s.body}>
        <p style={s.note}>
          Every account gets the same catalog of default (auto) data sources; the toggles below control
          which of them <strong>sync daily to BigQuery</strong>. Settings layer global → account → workspace,
          each level winning over the one before it, and every account and workspace also has an on/off
          switch that pauses daily sync for everything inside it. Changes apply to existing sources
          immediately and to newly created workspaces at seed time — sources left off still sync on demand.
        </p>

        <div style={s.filters}>
          <select
            style={s.select}
            data-testid="default-sources-scope"
            value={scope}
            disabled={saving}
            onChange={(e) => { setScope(e.target.value); setWsScope(WHOLE_ACCOUNT); }}
          >
            <option value={GLOBAL_SCOPE}>Global default (all accounts)</option>
            {storeOptions.map((st) => <option key={st.shopKey} value={st.shopKey}>{st.name}</option>)}
          </select>
          {shopScoped && (
            <select
              style={s.select}
              data-testid="default-sources-workspace-scope"
              value={wsScope}
              disabled={!shopState || saving}
              onChange={(e) => setWsScope(e.target.value)}
            >
              <option value={WHOLE_ACCOUNT}>Whole account</option>
              {workspaceOptions.map((ws) => (
                <option key={ws.id} value={ws.id}>
                  {ws.name || ws.id}
                  {ws.enabled === false ? ' • off' : ''}
                  {ws.overrideCount > 0 ? ` • ${ws.overrideCount} override${ws.overrideCount === 1 ? '' : 's'}` : ''}
                </option>
              ))}
            </select>
          )}
          <span style={s.scopeHint}>{scopeHint}</span>
        </div>

        {renderMasterBar()}

        <div style={s.card}>
          <table style={s.table}>
            <thead>
              <tr>
                <th style={s.th}>Default Source</th>
                <th style={s.th}>Report</th>
                <th style={s.th}>Catalog Default</th>
                <th style={{ ...s.th, textAlign: 'right' }}>Daily Sync</th>
              </tr>
            </thead>
            <tbody>
              {loading || !scopeReady ? (
                <tr><td style={s.empty} colSpan={4}>Loading default sources…</td></tr>
              ) : specs.length === 0 ? (
                <tr><td style={s.empty} colSpan={4}>No default sources in the catalog.</td></tr>
              ) : groups.map((group) => (
                <React.Fragment key={group.label}>
                  <tr><td style={s.groupRow} colSpan={4}>{group.label}</td></tr>
                  {group.specs.map((spec) => (
                    <tr key={spec.autoKey}>
                      <td style={s.td}>
                        <div style={s.name}>{spec.name}</div>
                        <div style={s.meta}>{spec.autoKey}</div>
                      </td>
                      <td style={{ ...s.td, ...s.meta }}>{spec.reportType}</td>
                      <td style={s.td}>
                        <span style={s.defaultPill(spec.specAutoSync)}>{spec.specAutoSync ? 'Daily' : 'On demand'}</span>
                      </td>
                      <td style={{ ...s.td, textAlign: 'right' }}>{renderControl(spec)}</td>
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// Normalisers for the shop / workspace GET+PUT payloads. `enabled` defaults to
// true (the server only stores it once an admin turns it off).
function toShopState(data, prev) {
  return {
    enabled: data?.enabled !== false,
    overrides: data?.overrides || {},
    effective: data?.effective || {},
    workspaces: Array.isArray(data?.workspaces) ? data.workspaces : (prev?.workspaces || []),
  };
}

function toWsState(data) {
  return {
    name: data?.name || '',
    enabled: data?.enabled !== false,
    shopEnabled: data?.shopEnabled !== false,
    overrides: data?.overrides || {},
    inherited: data?.inherited || {},
    effective: data?.effective || {},
  };
}
